// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chats.test.tsx
import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import {
  ChatCircle,
  MagnifyingGlass,
  Plus,
  Rows,
  SquaresFour,
  Trash,
} from "@phosphor-icons/react";
import {
  Button,
  Callout,
  Dialog,
  Flex,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { askPunttChatsRoute } from "./chatsRoute";
import {
  ListCardGridFooter,
  ListCardRowMembers,
  ListCardRowTimestamps,
} from "./components/ListCardGridFooter";
import { ListItemDeleteButton } from "./components/ListItemDeleteButton";
import { UnreadDot } from "./components/UnreadDot";
import { launcherSearch, type LauncherSearch } from "./launcherSearch";
import { askPunttRoute } from "./route";
import { askPunttChatNavLink } from "./utils/chatNavigation";
import {
  formatListCardAbsoluteDate,
  LIST_CARD_GRID_HEIGHT_CLASS,
  LIST_CARD_GRID_OPEN_BTN_CLASS,
  LIST_CARD_TITLE_DELETE_RESERVE_CLASS,
} from "./utils/listCardTimestamps";
import { projectMemberLabel } from "./utils/projectListMembers";

import { PageHeader } from "../../components/PageHeader";
import { useAppSelector } from "../../utils/hooks";
import {
  useAskPunttAllChats,
  useDeleteAskPunttChat,
  ASK_PUNTT_UNREAD_LIST_POLL_MS,
} from "../../utils/queries/askPuntt";

const CHAT_CARD_HOVER_SHELL =
  "relative w-full rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-puntt-accent-7 hover:shadow-md";
const CHAT_LIST_ROW_HOVER_SHELL =
  "relative w-full rounded-lg border border-puntt-neutral-gray-5 bg-base-white transition-all hover:border-puntt-accent-7 hover:shadow-sm";
const CHAT_CARD_OPEN_BTN = `${LIST_CARD_GRID_OPEN_BTN_CLASS} ${LIST_CARD_GRID_HEIGHT_CLASS}`;
const CHAT_LIST_ROW_OPEN_BTN =
  "flex w-full cursor-pointer items-center gap-4 rounded-[inherit] border-0 bg-transparent p-4 text-left shadow-none";

const VIEW_STORAGE_KEY = "askpuntt-chats-view";

type ChatsView = "grid" | "list";

type ChatListItem = {
  _id: string;
  title: string;
  userId?: string;
  ownerName?: string;
  role?: "owner" | "shared";
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
  createdAt?: string;
  updatedAt?: string;
  hasUnread?: boolean;
  members?: AskPunttChatUserSummary[];
};

function getStoredView(): ChatsView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // ignore
  }
  return "list";
}

export function formatChatDate(iso: string | undefined): string {
  return formatListCardAbsoluteDate(iso);
}

function chatSubtitle(
  chat: ChatListItem,
  currentUserId: string | undefined,
): string | null {
  if (chat.userId !== currentUserId && chat.ownerName) {
    return `Owned by ${chat.ownerName}`;
  }
  if (chat.role === "shared") {
    return "Shared with you";
  }
  if (chat.projectId && chat.projectChatKind === "personal") {
    return "Project chat";
  }
  return null;
}

function canModifyChat(
  chat: ChatListItem,
  currentUserId: string | undefined,
): boolean {
  // Only the chat owner may delete. Treat unresolved auth as not-owner
  // so we never flash controls on someone else's chat.
  return (
    currentUserId != null &&
    chat.userId != null &&
    chat.userId === currentUserId
  );
}

function ChatOwnerActions({
  chat,
  onRequestDelete,
}: {
  chat: ChatListItem;
  onRequestDelete: (chatId: string) => void;
}) {
  return (
    <div className="absolute right-3 top-3 z-10 flex items-center">
      <ListItemDeleteButton
        ariaLabel={`Delete chat ${chat.title}`}
        data-testid={`chat-row-delete-${chat._id}`}
        onClick={() => onRequestDelete(chat._id)}
      />
    </div>
  );
}

function ChatCard({
  chat,
  currentUserId,
  onOpen,
  onRequestDelete,
}: {
  chat: ChatListItem;
  currentUserId: string | undefined;
  onOpen: (chat: ChatListItem) => void;
  onRequestDelete: (chatId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const subtitle = chatSubtitle(chat, currentUserId);
  const members = chat.members ?? [];
  const canModify = canModifyChat(chat, currentUserId);

  return (
    <div
      className="group relative"
      data-testid={`chat-row-${chat._id}`}
      data-chat-id={chat._id}
    >
      <div
        className={CHAT_CARD_HOVER_SHELL}
        data-testid={`chat-row-surface-${chat._id}`}
      >
        <button
          type="button"
          onClick={() => onOpen(chat)}
          aria-label={t("chatsPage.openChat", { title: chat.title })}
          data-testid={`chat-row-open-${chat._id}`}
          className={CHAT_CARD_OPEN_BTN}
        >
          <div className="min-w-0">
            <Text
              as="div"
              size="4"
              weight="bold"
              className={`flex items-center gap-2 truncate text-puntt-neutral-gray-12${
                canModify ? ` ${LIST_CARD_TITLE_DELETE_RESERVE_CLASS}` : ""
              }`}
            >
              <span className="truncate">{chat.title}</span>
              {chat.hasUnread ? (
                <UnreadDot
                  testId={`chat-row-unread-${chat._id}`}
                  ariaLabel="Unread"
                />
              ) : null}
            </Text>
            {subtitle ? (
              <Text
                size="2"
                className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
                data-testid={`chat-row-${chat._id}-subtitle`}
              >
                {subtitle}
              </Text>
            ) : null}
          </div>
          <ListCardGridFooter
            idPrefix="chat-row"
            entityId={chat._id}
            updatedAt={chat.updatedAt}
            createdAt={chat.createdAt}
            members={members}
            memberLabel={projectMemberLabel}
            hideOnGroupHover={canModify}
          />
        </button>
        {canModify ? (
          <ChatOwnerActions chat={chat} onRequestDelete={onRequestDelete} />
        ) : null}
      </div>
    </div>
  );
}

function ChatListRow({
  chat,
  currentUserId,
  onOpen,
  onRequestDelete,
}: {
  chat: ChatListItem;
  currentUserId: string | undefined;
  onOpen: (chat: ChatListItem) => void;
  onRequestDelete: (chatId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const subtitle = chatSubtitle(chat, currentUserId);
  const members = chat.members ?? [];
  const canModify = canModifyChat(chat, currentUserId);

  return (
    <div
      className="group relative"
      data-testid={`chat-row-${chat._id}`}
      data-chat-id={chat._id}
    >
      <div
        className={CHAT_LIST_ROW_HOVER_SHELL}
        data-testid={`chat-row-surface-${chat._id}`}
      >
        <button
          type="button"
          onClick={() => onOpen(chat)}
          aria-label={t("chatsPage.openChat", { title: chat.title })}
          data-testid={`chat-row-open-${chat._id}`}
          className={CHAT_LIST_ROW_OPEN_BTN}
        >
          <div className="min-w-0 flex-1">
            <Text
              size="3"
              weight="medium"
              className={`flex items-center gap-2 truncate text-puntt-neutral-gray-12${
                canModify ? ` ${LIST_CARD_TITLE_DELETE_RESERVE_CLASS}` : ""
              }`}
            >
              <span className="truncate">{chat.title}</span>
              {chat.hasUnread ? (
                <UnreadDot
                  testId={`chat-row-unread-${chat._id}`}
                  ariaLabel="Unread"
                />
              ) : null}
            </Text>
            {subtitle ? (
              <Text
                size="1"
                className="block truncate text-puntt-neutral-gray-11"
                data-testid={`chat-row-${chat._id}-subtitle`}
              >
                {subtitle}
              </Text>
            ) : null}
          </div>
          <Flex align="center" gap="3" className="ml-auto shrink-0">
            <ListCardRowTimestamps
              idPrefix="chat-row"
              entityId={chat._id}
              updatedAt={chat.updatedAt}
              createdAt={chat.createdAt}
            />
            <ListCardRowMembers
              idPrefix="chat-row"
              entityId={chat._id}
              members={members}
              memberLabel={projectMemberLabel}
            />
          </Flex>
        </button>
        {canModify ? (
          <ChatOwnerActions chat={chat} onRequestDelete={onRequestDelete} />
        ) : null}
      </div>
    </div>
  );
}

export default function AskPunttChats() {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const urlSearch = useSearch({ strict: false }) as LauncherSearch;
  const [search, setSearch] = useState(urlSearch.q ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch.q ?? "");
  const view: ChatsView = urlSearch.view ?? getStoredView();
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);
  const { data, isLoading, isError, isFetching } = useAskPunttAllChats({
    refetchInterval: ASK_PUNTT_UNREAD_LIST_POLL_MS,
  });
  const deleteChatMutation = useDeleteAskPunttChat();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const navigate = useNavigate();

  const syncLauncherSearch = useCallback(
    (patch: Partial<LauncherSearch>) => {
      navigate({
        to: askPunttChatsRoute.to,
        search: launcherSearch({ ...urlSearch, ...patch }),
        replace: true,
      });
    },
    [navigate, urlSearch],
  );

  useEffect(() => {
    setSearch(urlSearch.q ?? "");
    setDebouncedSearch(urlSearch.q ?? "");
  }, [urlSearch.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const q = debouncedSearch.trim() || undefined;
    if (q === urlSearch.q) return;
    syncLauncherSearch({ q });
  }, [debouncedSearch, urlSearch.q, syncLauncherSearch]);

  const chats = data?.chats ?? [];
  const total = data?.total ?? 0;
  const chatToDelete = chats.find((c) => c._id === chatIdToDelete);

  const sortedChats = useMemo(
    () =>
      [...chats].sort(
        (a, b) =>
          new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
          new Date(a.updatedAt ?? a.createdAt ?? 0).getTime(),
      ),
    [chats],
  );

  const filteredChats = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return sortedChats;
    return sortedChats.filter((c) => c.title.toLowerCase().includes(q));
  }, [sortedChats, debouncedSearch]);

  const showAdminBanner =
    currentUserId !== undefined &&
    chats.some((c) => c.userId !== currentUserId);

  const isSearchPending = search.trim() !== debouncedSearch.trim();
  const hasFilters = debouncedSearch.trim().length > 0;
  const listLoading = isLoading || isFetching || isSearchPending;
  const showNoResults =
    !listLoading && !isError && hasFilters && filteredChats.length === 0;
  const showEmpty =
    !listLoading && !isError && !hasFilters && filteredChats.length === 0;

  const handleViewChange = (next: ChatsView) => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    syncLauncherSearch({ view: next });
  };

  const openChat = (chat: ChatListItem) => {
    navigate(askPunttChatNavLink(chat));
  };

  const handleNewChat = () => {
    navigate({ to: askPunttRoute.to });
  };

  const handleDeleteChat = (chatId: string) => {
    deleteChatMutation.mutate(chatId, {
      onSuccess: () => setChatIdToDelete(null),
    });
  };

  return (
    <div
      className="flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-chats"
    >
      <PageHeader className="justify-between gap-4">
        <Text size="5" weight="bold" className="text-puntt-accent-9">
          {t("chats")}
        </Text>
        <Flex align="center" gap="2" wrap="wrap">
          <TextField.Root
            data-testid="chats-search"
            placeholder={t("searchChats")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("searchChats")}
            className="min-w-[200px]"
          >
            <TextField.Slot>
              <MagnifyingGlass size={16} aria-hidden="true" />
            </TextField.Slot>
          </TextField.Root>

          <Flex
            gap="0"
            role="group"
            aria-label={t("chatView")}
            className="overflow-hidden rounded-md border border-puntt-neutral-gray-6"
          >
            <button
              type="button"
              data-testid="chats-view-grid"
              aria-label={t("gridView")}
              aria-pressed={view === "grid"}
              className={`cursor-pointer px-2.5 py-2 transition-colors ${
                view === "grid"
                  ? "bg-puntt-accent-3 text-puntt-accent-11"
                  : "bg-base-white text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-2"
              }`}
              onClick={() => handleViewChange("grid")}
            >
              <SquaresFour size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              data-testid="chats-view-list"
              aria-label={t("listView")}
              aria-pressed={view === "list"}
              className={`cursor-pointer border-l border-puntt-neutral-gray-6 px-2.5 py-2 transition-colors ${
                view === "list"
                  ? "bg-puntt-accent-3 text-puntt-accent-11"
                  : "bg-base-white text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-2"
              }`}
              onClick={() => handleViewChange("list")}
            >
              <Rows size={16} aria-hidden="true" />
            </button>
          </Flex>

          <Button
            data-testid="chats-new-button"
            onClick={handleNewChat}
            className="cursor-pointer"
          >
            <Plus size={14} weight="bold" aria-hidden="true" />
            {t("newChat")}
          </Button>
        </Flex>
      </PageHeader>

      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        {showAdminBanner && (
          <Callout.Root
            color="blue"
            size="1"
            className="mb-4"
            data-testid="ask-puntt-chats-admin-banner"
          >
            <Callout.Text>{t("chatsPage.adminViewingAllChats")}</Callout.Text>
          </Callout.Root>
        )}

        {total > chats.length && (
          <Text
            size="1"
            className="mb-3 block text-puntt-neutral-gray-9"
            data-testid="ask-puntt-chats-total"
          >
            {t("chatsPage.showingCount", { shown: chats.length, total })}
          </Text>
        )}

        {listLoading ? (
          <div
            className={
              view === "grid" ? "grid grid-cols-2 gap-4" : "flex flex-col gap-2"
            }
            data-testid="chats-skeleton"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={
                  view === "grid"
                    ? "h-40 animate-pulse rounded-2xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                    : "h-20 animate-pulse rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                }
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root
            color="red"
            size="1"
            data-testid="ask-puntt-chats-error"
          >
            <Callout.Text>{t("failedLoadChats")}</Callout.Text>
          </Callout.Root>
        ) : showEmpty ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
            data-testid="chats-empty"
          >
            <ChatCircle
              size={40}
              weight="duotone"
              className="text-puntt-neutral-gray-8"
              aria-hidden="true"
            />
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("noChatsYet")}
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              {t("startConversation")}
            </Text>
            <Button
              data-testid="chats-empty-new-button"
              onClick={handleNewChat}
              className="cursor-pointer"
            >
              <Plus size={14} weight="bold" aria-hidden="true" />
              {t("newChat")}
            </Button>
          </Flex>
        ) : showNoResults ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="3"
            className="mt-16"
            data-testid="chats-no-results"
          >
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("noChatsMatch")}
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              {t("tryAdjustingSearch")}
            </Text>
            <Button
              variant="soft"
              color="gray"
              data-testid="chats-clear-filters"
              aria-label={t("clearSearch")}
              className="cursor-pointer"
              onClick={() => setSearch("")}
            >
              {t("clearSearch")}
            </Button>
          </Flex>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 gap-4" data-testid="chats-grid">
            {filteredChats.map((chat) => (
              <ChatCard
                key={chat._id}
                chat={chat}
                currentUserId={currentUserId}
                onOpen={openChat}
                onRequestDelete={setChatIdToDelete}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="chats-list">
            {filteredChats.map((chat) => (
              <ChatListRow
                key={chat._id}
                chat={chat}
                currentUserId={currentUserId}
                onOpen={openChat}
                onRequestDelete={setChatIdToDelete}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog.Root
        open={chatIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setChatIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>{t("deleteChatQ")}</Dialog.Title>
          <Dialog.Description>
            {/* One interpolated sentence, not a fragment plus a bare "?" JSX
                node: the question mark and word order both move in Arabic and
                German. Same keys AppSidebar renders for this dialog. */}
            {chatToDelete
              ? tCommon("nav.areYouSureDeleteChat", {
                  title: chatToDelete.title,
                })
              : tCommon("nav.areYouSureDeleteChatFallback")}
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setChatIdToDelete(null)}
              data-testid="chats-delete-cancel"
            >
              {t("cancel")}
            </Button>
            <Button
              color="red"
              onClick={() =>
                chatIdToDelete != null && handleDeleteChat(chatIdToDelete)
              }
              disabled={deleteChatMutation.isPending}
              loading={deleteChatMutation.isPending}
              data-testid="chats-delete-confirm"
            >
              <Trash aria-hidden="true" />
              {t("delete")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
